import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// =============================================================================
//  Copyright : 2021 semantics GmbH
//  Date      : $Date: 2026-05-27 15:23:24 +0200 (Wed, 27 May 2026) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 163322 $
//  Purpose   : upload components
// =============================================================================
// ---Third-party imports
import React from 'react';
// ---Framework imports
import { Sel, Actions } from 'uix/registries';
import log from 'uix/logging';
import { size, callIfFunc } from 'ldash';
import { Dropper, XMenu, VIcon, ProgressMenuItem } from 'uix/components';
import { useState, useSelector, useActions, usePartContext, useCallback, useEffect, useTestableState } from 'uix/hooks';
import { prettyBytes } from 'uix/utils';
// ---Local imports
import { useUpload } from './hooks';
import { DirectoryTree } from './dirtree';
// ---Globals
export const MultiFileUploader = ({ onFilesChange, renderFileName, menuItems, progressMenu, mimetypes, treeProps = {} })=>{
    let { name: partName, type: partType } = usePartContext();
    let testPartId = partName || partType || 'global';
    let { uploadFiles, cancelUploads, progress, uploaded } = useUpload();
    let [droppedFiles, setDroppedFiles] = useTestableState([], ()=>`upload.${testPartId}.droppedFiles`);
    let onFilesChanged = useCallback((droppedFiles)=>{
        setDroppedFiles(droppedFiles);
    });
    useEffect(()=>{
        let files = droppedFiles.map(({ path, state, size })=>uploaded[path] ? {
                path,
                state: 'uploaded',
                size,
                uploadid: uploaded[path]
            } : {
                path,
                state,
                size
            });
        callIfFunc(onFilesChange, files);
    }, [
        droppedFiles,
        uploaded
    ]);
    useEffect(()=>()=>cancelUploads(), []);
    let dropperMenu = /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(XMenu.Item, {
                icon: [
                    'upload',
                    ...progress.running ? [
                        {
                            name: 'spinner',
                            loading: true,
                            color: 'green'
                        }
                    ] : []
                ],
                content: "Start upload",
                title: "Upload files to server",
                disabled: droppedFiles.length == 0 || progress.running,
                onClick: ()=>uploadFiles(droppedFiles)
            }),
            menuItems
        ]
    });
    let renderFileStatus = useCallback((fname, fdata)=>{
        let icon = /*#__PURE__*/ _jsx(VIcon, {
            name: "minus",
            color: "silver"
        });
        if (progress.activeUploads.some((u)=>u.fpath == fdata.path)) {
            icon = /*#__PURE__*/ _jsx(VIcon, {
                name: "spinner",
                color: "green",
                loading: true
            });
        } else if (uploaded[fdata.path]) {
            icon = /*#__PURE__*/ _jsx(VIcon, {
                name: "check"
            });
        }
        return icon;
    }, [
        uploaded,
        progress.activeUploads
    ]);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Dropper, {
                as: "Menu",
                onFilesChanged: onFilesChanged,
                moreMenu: dropperMenu,
                progressMenu: progressMenu || (progress.total > 0 ? /*#__PURE__*/ _jsx(ProgressMenuItem, {
                    total: progress.total,
                    value: progress.step,
                    suffix: ` Uploads${progress.rateBps > 0 ? ` ${prettyBytes(progress.rateBps)}/s` : ''}`,
                    position: "right"
                }) : null),
                mimetypes: mimetypes
            }),
            /*#__PURE__*/ _jsx(DirectoryTree, {
                attached: true,
                files: droppedFiles,
                collapseFiles: false,
                renderFileName: renderFileName,
                renderFileStatus: renderFileStatus,
                ...treeProps
            })
        ]
    });
};
export const SingleFileUploader = ({ children, onUploadDone, onUploadProgress, ...props })=>{
    let { name: partName } = usePartContext();
    let [fileid, setFileid] = useState(null);
    let uploadInfo = useSelector(Sel.uploads);
    let uploadFile = useActions(Actions.uploadFile);
    let uploadedFile = uploadInfo[fileid] ?? {};
    let takeFile = ({ path, bytes, size })=>{
        // path starts already with an / but a duplication is probably ok (for split below)
        let new_fileid = `${partName}/${path}`;
        if (new_fileid != fileid) {
            let chunksize = Math.max(256 * 1024, parseInt(size / 10));
            uploadFile(new_fileid, bytes, chunksize);
            setFileid(new_fileid);
        }
    };
    useEffect(()=>{
        if (uploadedFile.state == 'upload done' && onUploadDone) {
            onUploadDone({
                ...uploadedFile,
                filename: fileid.split('/').at(-1)
            });
        }
    }, [
        uploadedFile.state
    ]);
    useEffect(()=>{
        if (onUploadProgress) {
            let percentage = uploadedFile.chunk / uploadedFile.chunks * 100;
            onUploadProgress({
                percentage,
                total: uploadedFile.size,
                active: uploadedFile.chunks !== uploadedFile.chunk
            });
        }
    }, [
        uploadedFile.chunk
    ]);
    let onFilesChanged = useCallback((files)=>size(files) > 0 && takeFile(files[0]));
    return /*#__PURE__*/ _jsx(Dropper, {
        singleFile: true,
        onFilesChanged: onFilesChanged,
        ...props,
        children: children
    });
};
